import { useEffect, useRef, useState } from "react";
import { AUDIENCE_FALLBACK, AUDIENCE_WORDS } from "@/lib/industries";

interface WordCyclerProps {
  words?: string[];
  fallback?: string;
  intervalMs?: number;
  className?: string;
}

export function WordCycler({
  words = AUDIENCE_WORDS,
  fallback = AUDIENCE_FALLBACK,
  intervalMs = 2400,
  className = "",
}: WordCyclerProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [animating, setAnimating] = useState(true);
  const containerRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    if (reducedMotion || paused) return;
    const id = window.setInterval(() => {
      setAnimating(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % words.length);
        setAnimating(true);
      }, 220);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [reducedMotion, paused, words.length, intervalMs]);

  if (reducedMotion) {
    return <span className={className}>{fallback}</span>;
  }

  return (
    <span
      ref={containerRef}
      className={`relative inline-block align-baseline ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      aria-live="polite"
      aria-atomic="true"
      data-testid="word-cycler"
    >
      {/* Invisible sizer: reserves space for the longest word so there is no layout shift. */}
      <span aria-hidden="true" className="invisible whitespace-normal">
        {words.reduce((a, b) => (a.length >= b.length ? a : b))}
      </span>
      {/* Animated current word, absolutely positioned over the sizer. */}
      <span
        key={index}
        className={
          "absolute inset-0 transition-all duration-300 ease-out " +
          (animating ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-1")
        }
      >
        {words[index]}
      </span>
    </span>
  );
}
